import { CompositeFilterDescriptor, process, State } from '@progress/kendo-data-query';
import { GridDataStateChangeEvent, GridPagerSettings } from '@progress/kendo-react-grid';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import usePunchoutOrderGuideColumns from 'src/assets/static/punchout_order_guide/usePunchoutOrderGuideColumns';
import GridWrapper from 'src/components/GridWrapper';
import ClearFilterButton from 'src/components/design-system/ClearFilterButton';
import useGetPunchoutOrderGuideQuery from 'src/services/catalog_products/useGetPunchoutOrderGuideQuery';
import { PunchoutOrderGuideItem } from 'src/types/punchout-order-guide/PunchoutOrderGuideItem';
import {
  applyPunchoutAttributesFilter,
  buildPunchoutOrderGuideProcessState,
  extractAttributesFilter,
} from './punchoutOrderGuideUtils';

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 25, 50, 100],
};

const initialDataState: State = {
  skip: 0,
  take: 100,
  sort: [{ field: 'description', dir: 'asc' }],
};

const defaultProps = {
  tableTextId: 'Items',
  DataItemKey: 'rowId',
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  pageable: gridPagerSettings,
} as const;

interface PunchoutOrderGuideTableProps {
  catalogId: number;
  searchValue: string;
  onSearchChange: (value: string) => void;
}

const PunchoutOrderGuideTable: FC<PunchoutOrderGuideTableProps> = ({
  catalogId,
  searchValue,
  onSearchChange,
}) => {
  const { t } = useTranslation();
  const columns = usePunchoutOrderGuideColumns();
  const [dataState, setDataState] = useState<State>(initialDataState);
  const [prevSearchValue, setPrevSearchValue] = useState(searchValue);
  const [prevCatalogId, setPrevCatalogId] = useState(catalogId);
  const { data = [], isLoading, isFetching, isError } = useGetPunchoutOrderGuideQuery(catalogId);

  if (searchValue !== prevSearchValue || catalogId !== prevCatalogId) {
    setPrevSearchValue(searchValue);
    setPrevCatalogId(catalogId);
    setDataState((prev) => ({
      ...prev,
      skip: 0,
    }));
  }

  useEffect(() => {
    if (isError) {
      toast.error(t('Unable to load Punchout Order Guide. Please try again.'));
    }
  }, [isError, t]);

  const searchFilteredData = useMemo(() => {
    const trimmedSearch = searchValue.trim().toLowerCase();
    if (!trimmedSearch) {
      return data;
    }

    return data.filter((item) => {
      const itemNum = item.itemNum?.toLowerCase() ?? '';
      const description = item.description?.toLowerCase() ?? '';
      return itemNum.includes(trimmedSearch) || description.includes(trimmedSearch);
    });
  }, [data, searchValue]);

  const attributesFilter = useMemo(
    () => extractAttributesFilter(dataState.filter),
    [dataState.filter]
  );

  const attributeFilteredData = useMemo(
    () => applyPunchoutAttributesFilter(searchFilteredData, attributesFilter),
    [searchFilteredData, attributesFilter]
  );

  const processDataState = useMemo(
    () => buildPunchoutOrderGuideProcessState(dataState),
    [dataState]
  );

  const { data: gridData, total } = useMemo(
    () => process<PunchoutOrderGuideItem>(attributeFilteredData, processDataState),
    [attributeFilteredData, processDataState]
  );

  const hasSearchValue = Boolean(searchValue.trim());
  const hasGridFilters = Boolean(
    (dataState.filter as CompositeFilterDescriptor | undefined)?.filters?.length
  );
  const hasActiveFilters = hasGridFilters || hasSearchValue;

  const handleClearFilters = useCallback(() => {
    onSearchChange('');
    setDataState((prev) => ({
      ...prev,
      filter: undefined,
      skip: 0,
    }));
  }, [onSearchChange]);

  const handleDataStateChange = useCallback((event: GridDataStateChangeEvent) => {
    setDataState(event.dataState);
  }, []);

  const gridToolbarChildren = useMemo(
    () => (
      <div className="flex w-full flex-row flex-wrap items-center gap-x-2 gap-y-2">
        <ClearFilterButton onClick={handleClearFilters} disabled={!hasActiveFilters} />
      </div>
    ),
    [handleClearFilters, hasActiveFilters]
  );

  return (
    <main className="flex h-full min-h-0 flex-col px-4 pb-4">
      {hasSearchValue && (
        <div className="mb-2 shrink-0 truncate text-sm font-[600]">
          {t('Showing results for')} &quot;{searchValue.trim()}&quot;
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-hidden">
        <GridWrapper
          id="punchoutOrderGuideTable"
          className="h-full min-h-0"
          columns={columns}
          dataSource={isError ? [] : gridData}
          total={isError ? 0 : total}
          dataState={dataState}
          onDataStateChange={handleDataStateChange}
          height="100%"
          loading={isLoading || isFetching}
          gridToolbarChildren={gridToolbarChildren}
          {...defaultProps}
          noRecordText={t('No items found')}
          scrollable="scrollable"
        />
      </div>
    </main>
  );
};

export default PunchoutOrderGuideTable;
